import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Film,
  Activity,
  ListChecks,
  Timer,
  Trash2,
  ArrowUpRight,
  Database,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { listSequences, getStats, deleteSequence } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";
import Heatmap from "@/components/Heatmap";
import AnchorPoolTile from "@/components/AnchorPoolTile";
import ShadowMonitorTile from "@/components/ShadowMonitorTile";
import PlayerNetworkGraph from "@/components/PlayerNetworkGraph";
import PassStats from "@/components/PassStats";
import HoverPreview from "@/components/HoverPreview";
import DatasetStatsTile from "@/components/DatasetStatsTile";
import TrainingRunsPanel from "@/components/TrainingRunsPanel";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track which row is currently hovered so we only mount ONE preview
  // `<video>` at a time. Without this guard the Library table would
  // spin up N parallel MP4 streams on page load — terrible for B2
  // bandwidth and the user's bandwidth.
  const [hoveredRow, setHoveredRow] = useState(null);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([getStats(), listSequences()]);
      setStats(s);
      setItems(l);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id) => {
    await deleteSequence(id);
    toast.success("Sequence deleted");
    refresh();
  };

  return (
    <div data-testid={TID.dashboardRoot} className="min-h-screen">
      {/* Header */}
      <header className="px-8 pt-8 pb-6 border-b border-border">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Admin Board · Phase 1
            </div>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">
              Tactic Cutter<span className="text-primary">.</span>
            </h1>
            <p className="mt-2 text-muted-foreground text-sm max-w-xl">
              Cut, calibrate, track and label ultimate frisbee sequences into ML-ready datasets.
            </p>
          </div>
          <div className="flex items-center gap-2">
          <Button
            data-testid={TID.newSequenceBtn}
            onClick={() => navigate("/admin/wizard")}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
          >
            <Plus className="w-4 h-4 mr-2" /> New Match
            </Button>
            <HelpButton topic="dashboard" />
          </div>
        </div>
      </header>

      <div className="px-8 py-6 space-y-8">
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testid={TID.statsTotal}
            icon={Film}
            label="Matches"
            value={stats?.total_matches ?? "—"}
            sub="Uploaded videos"
          />
          <StatCard
            testid={TID.statsTracked}
            icon={Activity}
            label="Sequences"
            value={stats?.total_clips ?? "—"}
            sub="Cut · tracked · labeled"
            accent
          />
          <StatCard
            testid={TID.statsLabeled}
            icon={ListChecks}
            label="Labeled"
            value={stats?.labeled ?? "—"}
            sub="Ready for ML"
          />
          <StatCard
            testid={TID.statsDuration}
            icon={Timer}
            label="Footage"
            value={`${(stats?.total_duration_s ?? 0).toFixed(0)}s`}
            sub="Total cut duration"
          />
        </section>

        {/* Passing-Flow-Map + Pass-Statistics Cockpit-Row */}
        <section className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Heatmap />
          </div>
          <div className="lg:col-span-1">
            <PassStats />
          </div>
        </section>

        {/* Player Network Graph — force-directed view of the team's pass
            hierarchy. Lives on its own row because it can grow to ~720px
            when a roster of 14 players is fully connected and tabletop
            stacking against the Heatmap would clip both. */}
        <section>
          <PlayerNetworkGraph />
        </section>

        {/* Data-Flywheel — Phase 4 Schritt 2. Retraining-Pool-Zähler
            (Dual-Pfad-Export) + Shadow-Monitor (dl_auto-Reife). */}
        <section className="grid lg:grid-cols-2 gap-6">
          <AnchorPoolTile />
          <ShadowMonitorTile />
        </section>

        {/* Dataset-Stats — Phase B prep tile. Surfaces the health and
            size of the verified-pass training corpus that the Phase A
            harvester has been building. Coach reads this to know when
            the corpus is big enough to start training the disc-detection
            YOLO. */}
        <section>
          <DatasetStatsTile />
        </section>

        {/* ML Training Control Center — Phase B · Step 1. Sits directly
            below the dataset stats so the coach sees corpus health → then
            kicks off a training run from the same scroll position. */}
        <section>
          <TrainingRunsPanel />
        </section>

        {/* Library */}
        <section>
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Database · Matches
              </div>
              <h2 className="font-display font-black uppercase text-xl tracking-wider mt-1">
                Match Library
              </h2>
            </div>
            <Link
              to="/admin/library"
              className="font-mono text-[10px] uppercase tracking-widest text-primary hover:text-foreground transition-colors flex items-center gap-1"
            >
              View all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="border border-border bg-card rounded-sm overflow-hidden">
            <Table data-testid={TID.libraryTable}>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ID</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Format</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Division</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Sequences</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Labeled</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Duration</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</TableHead>
                  <TableHead className="text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-12 font-mono text-xs">Loading…</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-16 text-center">
                      <Database className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1.5} />
                      <div className="font-display font-black uppercase tracking-widest text-foreground">No matches yet</div>
                      <div className="text-sm text-muted-foreground mt-1">Upload your first match to start cutting tactical sequences.</div>
                      <Button
                        onClick={() => navigate("/admin/wizard")}
                        className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Create Match
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : items.map((s) => (
                  <TableRow
                    key={s.id}
                    data-testid={TID.libraryRow(s.id)}
                    className="border-border hover:bg-secondary/50 transition-colors relative group"
                    onMouseEnter={() => s.preview_clip_id && setHoveredRow(s.id)}
                    onMouseLeave={() => setHoveredRow((cur) => (cur === s.id ? null : cur))}
                  >
                    <TableCell className="font-mono text-xs text-primary relative">
                      #{s.id.slice(0, 8)}
                      {/* Hover-preview tooltip — anchored to the ID cell so
                          coaches can park their cursor without obscuring
                          the rest of the row. Only mounts the <video>
                          when this specific row is hovered. */}
                      {hoveredRow === s.id && s.preview_clip_id && (
                        <div
                          data-testid={`library-hover-preview-${s.id}`}
                          className="absolute left-0 top-full mt-1 z-30 w-64 pointer-events-none animate-in fade-in zoom-in-95 duration-150"
                        >
                          <div className="bg-card/95 backdrop-blur border border-pink-400/60 rounded-sm overflow-hidden shadow-xl shadow-pink-400/10">
                            <HoverPreview
                              clipId={s.preview_clip_id}
                              className="w-full aspect-video"
                              fallbackLabel="render queued"
                            />
                            <div className="px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-pink-300">
                              ▸ first tracked sequence
                            </div>
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{s.format || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{s.gender || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-foreground tabular-nums">{s.num_clips ?? 0}</TableCell>
                    <TableCell className="font-mono text-xs text-foreground tabular-nums">{s.num_labeled ?? 0}</TableCell>
                    <TableCell className="font-mono text-xs">{s.duration_s ? `${s.duration_s.toFixed(0)}s` : "—"}</TableCell>
                    <TableCell><StatusBadge s={s.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={TID.rowOpenBtn(s.id)}
                        size="sm"
                        variant="ghost"
                        className="hover:bg-accent hover:text-primary"
                        onClick={() => navigate(`/admin/wizard/${s.id}`)}
                      >
                        Open
                      </Button>
                      <Button
                        data-testid={TID.rowDeleteBtn(s.id)}
                        size="sm"
                        variant="ghost"
                        className="hover:bg-destructive/20 hover:text-destructive"
                        onClick={() => handleDelete(s.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border bg-card rounded-sm p-5 ${accent ? "border-primary/40" : "border-border"}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <Icon className={`w-4 h-4 ${accent ? "text-primary" : "text-muted-foreground"}`} strokeWidth={2} />
      </div>
      <div className="font-display font-black text-4xl tracking-tight tabular-nums">
        {value}
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {sub}
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    draft: { c: "bg-secondary text-muted-foreground border-border", l: "Draft" },
    uploaded: { c: "bg-secondary text-foreground border-border", l: "Uploaded" },
    calibrated: { c: "bg-secondary text-foreground border-border", l: "Calibrated" },
    ready: { c: "bg-primary/15 text-primary border-primary/30", l: "Ready" },
  };
  const { c, l } = map[s] || map.draft;
  return <Badge className={`${c} border font-mono text-[10px] uppercase tracking-widest`}>{l}</Badge>;
}
